import React, { useEffect, useReducer } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { withErrorBoundary } from 'react-error-boundary';
import Button from '@/components/Button/Button';
import validationApi from '@/validation-service/services/validationApi';
import useAlert from '@/hooks/useAlert';
import postValidateBatchReducer from '@/dashboard/components/ValidateBatchButtonLoader/postValidateBatchReducer';
import { useDashboardContext } from '@/dashboard/provider/DashboardContextProvider';
import { useDispatchValidateBatchBooleans } from '@/dashboard/components/ValidateBatchButtonLoader/useDispatchValidateBatchBooleans';
import tokens from '@/dashboard/tokens.json';
import { hasPermission } from '@/authorization/AuthorizationGate/AuthorizationGate';
import { SCOPES } from '@/authorization/authorization.constants';

type ValidateBatchButtonLoaderProps = {
  readableMonth: string;
  employerId: number;
  visibility: boolean | null;
};

function ValidateBatchButtonLoader({
  readableMonth,
  employerId,
  visibility,
}: ValidateBatchButtonLoaderProps) {
  const { batchSubmitActions } = useDashboardContext();
  const [{ severity, description }, dispatch] = useReducer(postValidateBatchReducer, {
    severity: 'error',
    description: '',
  });
  const [validateBatch, { ...validateBatchBooleans }] = validationApi.useValidateBatchMutation();
  const { isLoading, isSuccess, isError, isUninitialized } = validateBatchBooleans;
  const canValidate = hasPermission([SCOPES.VALIDATE_BATCH]);

  useAlert({
    mui: {
      severity,
    },
    custom: {
      description,
    },
    app: {},
    dependencies: [severity, description],
    condition: () => !isUninitialized && (isSuccess || isError) && description != '',
  });

  useEffect(() => {
    if (
      (severity === 'error' || severity === 'success' || severity === 'info') &&
      description != ''
    ) {
      batchSubmitActions.submitEnd();
    }
  }, [severity, description]);

  function onValidateClick() {
    void validateBatch({ employerId });
  }

  useDispatchValidateBatchBooleans(validateBatchBooleans, dispatch);

  const ariaLabel = isLoading ? `Validating, ${readableMonth}` : `Validate ${readableMonth}`;
  if (visibility === true) {
    return (
      <Button
        variant="contained"
        onClick={canValidate ? onValidateClick : undefined}
        title={ariaLabel}
        aria-label={ariaLabel}
        disabled={isLoading || !canValidate}
      >
        {isLoading ? <CircularProgress size={20} color="inherit" sx={{ mr: 2 }} /> : null} Validate
      </Button>
    );
  } else if (visibility === false) {
    return (
      <Button variant="contained" disabled>
        Validate
      </Button>
    );
  } else {
    return <></>;
  }
}

function ErrorFallback() {
  useAlert({
    mui: {
      severity: 'error',
    },
    custom: {
      description: tokens['GENERAL_ERROR'],
    },
    app: {},
    dependencies: [],
    condition: () => true,
  });
  return (
    <Button variant="contained" disabled>
      Validate
    </Button>
  );
}

export default withErrorBoundary(ValidateBatchButtonLoader, {
  fallback: <ErrorFallback />,
});
